// Libraries
import { CommonModule } from '@angular/common';
import {
    Component,
    effect,
    ElementRef,
    inject,
    Signal,
    ViewChild
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// Components
import { Pop } from 'src/app/classes/shared/pop';
import { CalibrationComponent } from 'src/app/components/project-pages/viewer-maps/dialogs/calibration/calibration.component';
import { MaskEditorComponent } from 'src/app/components/project-pages/viewer-maps/dialogs/mask-editor/mask-editor.component';
import { Model } from 'src/app/interfaces/api/model';
import { ModelService } from 'src/app/services/api/models/model.service';
import { EditorMode, MapStore } from 'src/app/services/stores/map.store';
import { FileInputComponent } from 'ui-library';

@Component({
    selector: 'app-overlay-dialog',
    templateUrl: './overlay-dialog.component.html',
    styleUrls: ['./overlay-dialog.component.scss'],
    imports: [
        CommonModule,
        MaskEditorComponent,
        CalibrationComponent,
        MatTooltipModule,
        MatIconModule,
        TranslateModule,
        FileInputComponent,
    ],
    standalone: true,
})

export class OverlayDialogComponent {
    // Services
    private readonly mapStore: MapStore = inject(MapStore);
    private readonly dialogRef: MatDialogRef<OverlayDialogComponent> = inject(MatDialogRef<OverlayDialogComponent>);
    private readonly modelService: ModelService = inject(ModelService);
    private readonly pop: Pop = inject(Pop);
    private readonly translate: TranslateService = inject(TranslateService);

    // Components API
    @ViewChild('calibrationMapContainer') calibrationMapContainer!: ElementRef;
    @ViewChild(FileInputComponent) fileInputComponent!: FileInputComponent;

    // Signals
    public readonly mode: Signal<EditorMode> = this.mapStore.editorMode;
    public readonly currentModel: Signal<Model | null> = this.mapStore.currentModel;
    public readonly isDirty: Signal<boolean> = this.mapStore.isDirty;

    // Variables
    imageUrl: string = '';
    private ignoreNextDelete: boolean = false;

    constructor() {
        this.mapStore.setEditorMode('none');

        effect(() => {
            const model = this.currentModel();
            const src = model?.mapImageSrc || model?.mapImage;
            if (!src) return;

            this.mapStore.loadImageInfo(src).then(() => {
                this.imageUrl = src;
            });
        });
    }

    setMode(mode: EditorMode) {
        if (mode === this.mode()) {
            this.mapStore.setEditorMode('none');
        } else if (mode === 'transform') {
            this.dialogRef.close(false);
            this.mapStore.setEditorMode('transform');
        }
        else {
            this.mapStore.setEditorMode(mode);
        }
    }

    save(): void {
        this.mapStore.setEditorMode('none');
        this.dialogRef.close(true);
    }

    cancel(): void {
        this.mapStore.setEditorMode('none');
        this.dialogRef.close(false);
    }

    onFileSelected(files: FileList): void {
        if (!files || !files[0]) return;

        const file = files[0];
        const maxSizeInBytes = 4 * 1024 * 1024;

        if (file.size > maxSizeInBytes) {
            this.pop.toast(this.translate.instant('form.error.size_limit_4'), "danger", 4000, "upload");
            this.resetFileInput();
            return;
        }

        const model = this.currentModel();
        if (!model) return;

        this.modelService.uploadImage('mapImageSrc', file, model.id).subscribe({
            next: (res: any) => {
                const centerLat = model.mapCenter?.lat ?? model.origin?.position?.lat ?? 0;
                const centerLon = model.mapCenter?.lon ?? model.origin?.position?.lon ?? 0;
                const coordOffset = 0.0002;

                const updatedModel: Model = {
                    ...model,
                    mapImageSrc: res.model.mapImageSrc,
                    mapImage: res.model.mapImageSrc,
                    mapCenter: { lat: centerLat, lon: centerLon },
                    mapCoord: {
                        north: centerLat + coordOffset,
                        south: centerLat - coordOffset,
                        east: centerLon + coordOffset,
                        west: centerLon - coordOffset
                    },
                    imageTransform: {
                        angle: 0,
                        opacity: 1,
                        masks: []
                    }
                };

                this.mapStore.setCurrentModel(updatedModel);
                this.mapStore.notifyImageUploaded();
            },
            error: (err: any) => {
                console.error(err);
                this.ignoreNextDelete = true;

                if (err.error?.error?.includes('Unsupported MIME type')) {
                    this.pop.toast(this.translate.instant('viewer.upload_image_type_error'), "danger", 4000, "upload");
                }

                this.resetFileInput();
            }
        });
    }

    deleteImage(): void {
        if (this.ignoreNextDelete) {
            this.ignoreNextDelete = false;
            return;
        }

        this.mapStore.deleteImageFromModel();
        this.resetFileInput();
        this.setMode('none');
    }

    private resetFileInput(): void {
        if (this.fileInputComponent) {
            this.fileInputComponent.onDelete();
        }
    }

    canDeleteImage(): boolean {
        const model = this.currentModel();
        return !!(model?.mapImageSrc);
    }
}